export interface AccordionOptions {
  /** When true (default), opening an item closes any other open item in the same accordion. */
  singleOpen?: boolean;
}

const DEFAULT_OPTIONS: Required<AccordionOptions> = { singleOpen: true };

let idCounter = 0;

function setExpandedHeight(content: HTMLElement): void {
  const inner = content.querySelector<HTMLElement>(".accordion-content-inner");
  content.style.maxHeight = inner ? `${inner.scrollHeight}px` : "";
}

function openItem(item: HTMLElement): void {
  const trigger = item.querySelector<HTMLButtonElement>(".accordion-trigger");
  const content = item.querySelector<HTMLElement>(".accordion-content");
  if (!trigger || !content) return;
  item.classList.add("is-open");
  trigger.setAttribute("aria-expanded", "true");
  setExpandedHeight(content);
}

function closeItem(item: HTMLElement): void {
  if (item.dataset.alwaysOpen !== undefined) return;
  const trigger = item.querySelector<HTMLButtonElement>(".accordion-trigger");
  const content = item.querySelector<HTMLElement>(".accordion-content");
  if (!trigger || !content) return;
  item.classList.remove("is-open");
  trigger.setAttribute("aria-expanded", "false");
  content.style.maxHeight = "0px";
}

/** Scrolls a trigger back into view if opening its panel pushed it under the sticky header. */
function keepTriggerVisible(trigger: HTMLElement): void {
  const header = document.querySelector<HTMLElement>(".site-header");
  const headerHeight = header?.getBoundingClientRect().height ?? 0;
  const rect = trigger.getBoundingClientRect();
  if (rect.top < headerHeight) {
    window.scrollBy({ top: rect.top - headerHeight - 12, behavior: "smooth" });
  }
}

/**
 * Wires up `.accordion-item` widgets under `root` (buttons, keyboard nav, ARIA wiring,
 * dynamic height). Returns a dispose function that removes all listeners and observers.
 *
 * Keyboard support on a focused trigger: ArrowUp/ArrowDown move focus to the previous/next
 * trigger in the same accordion (wrapping at the ends), Home/End jump to the first/last
 * trigger, and Enter/Space toggle the focused item open/closed (native `<button>` behavior,
 * no extra wiring needed here).
 */
export function initAccordion(root: ParentNode = document, options: AccordionOptions = {}): () => void {
  const { singleOpen } = { ...DEFAULT_OPTIONS, ...options };
  const cleanupFns: Array<() => void> = [];

  root.querySelectorAll<HTMLElement>(".accordion-item").forEach((item) => {
    const trigger = item.querySelector<HTMLButtonElement>(".accordion-trigger");
    const content = item.querySelector<HTMLElement>(".accordion-content");
    const inner = content?.querySelector<HTMLElement>(".accordion-content-inner");
    if (!trigger || !content || !inner) return;

    idCounter += 1;
    if (!trigger.id) trigger.id = `accordion-trigger-${idCounter}`;
    if (!content.id) content.id = `accordion-panel-${idCounter}`;
    trigger.setAttribute("aria-controls", content.id);
    content.setAttribute("role", "region");
    content.setAttribute("aria-labelledby", trigger.id);

    const group = item.closest(".accordion");
    const siblings = group ? Array.from(group.querySelectorAll<HTMLElement>(".accordion-item")) : [item];

    if (item.dataset.alwaysOpen !== undefined) {
      openItem(item);
      trigger.disabled = true;
      return;
    }

    const handleClick = (): void => {
      const isOpen = item.classList.contains("is-open");
      if (isOpen) {
        closeItem(item);
        return;
      }
      if (singleOpen) {
        siblings.forEach((sibling) => {
          if (sibling !== item) closeItem(sibling);
        });
      }
      openItem(item);
      keepTriggerVisible(trigger);
    };

    const handleKeydown = (event: KeyboardEvent): void => {
      const triggers = siblings
        .map((sibling) => sibling.querySelector<HTMLButtonElement>(".accordion-trigger"))
        .filter((el): el is HTMLButtonElement => el !== null && !el.disabled);
      const currentIndex = triggers.indexOf(trigger);
      if (currentIndex === -1) return;

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          triggers[(currentIndex + 1) % triggers.length]?.focus();
          break;
        case "ArrowUp":
          event.preventDefault();
          triggers[(currentIndex - 1 + triggers.length) % triggers.length]?.focus();
          break;
        case "Home":
          event.preventDefault();
          triggers[0]?.focus();
          break;
        case "End":
          event.preventDefault();
          triggers[triggers.length - 1]?.focus();
          break;
      }
    };

    trigger.addEventListener("click", handleClick);
    trigger.addEventListener("keydown", handleKeydown);

    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(() => {
            if (item.classList.contains("is-open")) setExpandedHeight(content);
          });
    resizeObserver?.observe(inner);

    cleanupFns.push(() => {
      trigger.removeEventListener("click", handleClick);
      trigger.removeEventListener("keydown", handleKeydown);
      resizeObserver?.disconnect();
    });
  });

  return () => cleanupFns.forEach((dispose) => dispose());
}
